import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { 
  ChevronRight, 
  ChevronLeft, 
  Heart, 
  Sparkles, 
  CheckCircle,
  X,
  Dumbbell,
  Mail,
  Target,
  Briefcase,
  Activity,
  AlertCircle
} from 'lucide-react';
import './CoachingInquiryForm.css';

const CoachingInquiryForm = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [showRejection, setShowRejection] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const { register, handleSubmit, formState: { errors }, trigger } = useForm();

  const steps = [
    {
      id: 'gender',
      title: 'Ready to Transform with FWD? 💪',
      subtitle: 'Let\'s start your fitness journey with Dee',
      icon: <Heart className="w-8 h-8" />,
      fields: ['gender']
    },
    {
      id: 'basic-info',
      title: 'Tell Us About You ✨',
      subtitle: 'Your basic information',
      icon: <Sparkles className="w-8 h-8" />,
      fields: ['name', 'age']
    },
    {
      id: 'contact',
      title: 'How Can We Reach You? 📱',
      subtitle: 'Your contact details',
      icon: <Mail className="w-8 h-8" />,
      fields: ['phone', 'email', 'country']
    },
    {
      id: 'fitness-level',
      title: 'Your Fitness Journey 🏋️‍♀️',
      subtitle: 'Tell us about your gym experience',
      icon: <Dumbbell className="w-8 h-8" />,
      fields: ['gymExperience']
    },
    {
      id: 'goals',
      title: 'What Are Your Dreams? 🎯',
      subtitle: 'Your health and fitness goals',
      icon: <Target className="w-8 h-8" />,
      fields: ['fitnessGoals']
    },
    {
      id: 'work',
      title: 'What Do You Do? 💼',
      subtitle: 'Your profession',
      icon: <Briefcase className="w-8 h-8" />,
      fields: ['work']
    },
    {
      id: 'coaching-experience',
      title: 'Previous Coaching? 🤝',
      subtitle: 'Your coaching experience',
      icon: <Activity className="w-8 h-8" />,
      fields: ['previousCoaching']
    },
    {
      id: 'medical',
      title: 'Health Information 🏥',
      subtitle: 'Important medical details',
      icon: <AlertCircle className="w-8 h-8" />,
      fields: ['medicalConditions']
    },
    {
      id: 'commitment',
      title: 'How Ready Are You? 🔥',
      subtitle: 'Your commitment level',
      icon: <CheckCircle className="w-8 h-8" />,
      fields: ['keenness']
    },
    {
      id: 'confirmation',
      title: 'Almost There! ✅',
      subtitle: 'Confirm your information',
      icon: <CheckCircle className="w-8 h-8" />,
      fields: ['confirmation']
    }
  ];

  const currentFields = steps[currentStep]?.fields || [];

  const validateCurrentStep = async () => {
    const result = await trigger(currentFields);
    return result;
  };

  const nextStep = async () => {
    const isValid = await validateCurrentStep();
    if (isValid && currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleGenderSelect = (gender) => {
    if (gender === 'male') {
      setShowRejection(true);
    } else {
      nextStep();
    }
  };

  const onSubmit = async (data) => {
    try {
      setIsSubmitting(true);
      await addDoc(collection(db, 'coaching-inquiries'), {
        ...data,
        gender: 'female', // Since only females can reach the submit step
        timestamp: new Date(),
        status: 'new'
      });
      setSubmitSuccess(true);
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (showRejection) {
    return (
      <motion.div 
        className="rejection-screen"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div className="rejection-content">
          <motion.div
            initial={{ rotate: 0 }}
            animate={{ rotate: [0, -10, 10, -10, 0] }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <X className="w-24 h-24 text-pink-500" />
          </motion.div>
          <h2>Sorry! 😅</h2>
          <p>Fit with Dee specializes in training women only! 💪✨</p>
          <p>But huge respect for checking out FWD! Keep crushing your goals! 🙌</p>
          <button 
            onClick={() => setShowRejection(false)}
            className="back-button"
          >
            Go Back
          </button>
        </div>
      </motion.div>
    );
  }

  if (submitSuccess) {
    return (
      <motion.div 
        className="success-screen"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="success-content">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
          >
            <CheckCircle className="w-24 h-24 text-green-500" />
          </motion.div>
          <h2>Welcome to FWD! 🎉</h2>
          <p>Your application has been submitted successfully!</p>
          <p>Dee will personally review your goals and get back to you within 24 hours. Your transformation starts now! 💪✨</p>
        </div>
      </motion.div>
    );
  }

  return (
    <div className="form-container">
      <div className="form-wrapper">
        {/* Progress Bar */}
        <div className="progress-container">
          <div className="progress-bar">
            <motion.div 
              className="progress-fill"
              initial={{ width: 0 }}
              animate={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
          <span className="progress-text">
            Step {currentStep + 1} of {steps.length}
          </span>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="inquiry-form">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="step-container"
            >
              {/* Step Header */}
              <div className="step-header">
                <div className="step-icon">
                  {steps[currentStep].icon}
                </div>
                <h2>{steps[currentStep].title}</h2>
                <p>{steps[currentStep].subtitle}</p>
              </div>

              {/* Step Content */}
              <div className="step-content">
                {currentStep === 0 && (
                  <div className="gender-selection">
                    <h3>What's your gender?</h3>
                    <div className="gender-options">
                      <button
                        type="button"
                        className="gender-button female"
                        onClick={() => handleGenderSelect('female')}
                      >
                        <span className="gender-emoji">👩</span>
                        <span>Female</span>
                      </button>
                      <button
                        type="button"
                        className="gender-button male"
                        onClick={() => handleGenderSelect('male')}
                      >
                        <span className="gender-emoji">👨</span>
                        <span>Male</span>
                      </button>
                    </div>
                  </div>
                )}

                {currentStep === 1 && (
                  <div className="form-fields">
                    <div className="field-group">
                      <label htmlFor="name">What's your name? ✨</label>
                      <input
                        id="name"
                        {...register('name', { required: 'Name is required' })}
                        placeholder="Enter your beautiful name"
                        className={errors.name ? 'error' : ''}
                      />
                      {errors.name && <span className="error-message">{errors.name.message}</span>}
                    </div>
                    
                    <div className="field-group">
                      <label htmlFor="age">How old are you? 🎂</label>
                      <input
                        id="age"
                        type="number"
                        {...register('age', { 
                          required: 'Age is required',
                          min: { value: 16, message: 'Must be at least 16 years old' },
                          max: { value: 65, message: 'Must be under 65 years old' }
                        })}
                        placeholder="Your age"
                        className={errors.age ? 'error' : ''}
                      />
                      {errors.age && <span className="error-message">{errors.age.message}</span>}
                    </div>
                  </div>
                )}

                {currentStep === 2 && (
                  <div className="form-fields">
                    <div className="field-group">
                      <label htmlFor="phone">Your contact number (with country code) 📱</label>
                      <input
                        id="phone"
                        {...register('phone', { 
                          required: 'Phone number is required',
                          pattern: {
                            value: /^\+[1-9]\d{1,14}$/,
                            message: 'Please include country code (e.g., +1234567890)'
                          }
                        })}
                        placeholder="+1234567890"
                        className={errors.phone ? 'error' : ''}
                      />
                      {errors.phone && <span className="error-message">{errors.phone.message}</span>}
                    </div>
                    
                    <div className="field-group">
                      <label htmlFor="email">Your email address 📧</label>
                      <input
                        id="email"
                        type="email"
                        {...register('email', { 
                          required: 'Email is required',
                          pattern: {
                            value: /^\S+@\S+$/i,
                            message: 'Please enter a valid email'
                          }
                        })}
                        placeholder="your.email@example.com"
                        className={errors.email ? 'error' : ''}
                      />
                      {errors.email && <span className="error-message">{errors.email.message}</span>}
                    </div>

                    <div className="field-group">
                      <label htmlFor="country">What country are you based in? 🌍</label>
                      <input
                        id="country"
                        {...register('country', { required: 'Country is required' })}
                        placeholder="Your country"
                        className={errors.country ? 'error' : ''}
                      />
                      {errors.country && <span className="error-message">{errors.country.message}</span>}
                    </div>
                  </div>
                )}

                {currentStep === 3 && (
                  <div className="form-fields">
                    <div className="field-group">
                      <label>How long have you been going to the gym? 🏋️‍♀️</label>
                      <div className="radio-group">
                        <label className="radio-label">
                          <input
                            type="radio"
                            value="beginner"
                            {...register('gymExperience', { required: 'Please select your experience level' })}
                          />
                          <span className="radio-custom"></span>
                          <span className="radio-text">Beginner (0-6 months)</span>
                        </label>
                        <label className="radio-label">
                          <input
                            type="radio"
                            value="intermediate"
                            {...register('gymExperience', { required: 'Please select your experience level' })}
                          />
                          <span className="radio-custom"></span>
                          <span className="radio-text">Intermediate (6 months - 2 years)</span>
                        </label>
                        <label className="radio-label">
                          <input
                            type="radio"
                            value="advanced"
                            {...register('gymExperience', { required: 'Please select your experience level' })}
                          />
                          <span className="radio-custom"></span>
                          <span className="radio-text">Advanced (2+ years)</span>
                        </label>
                      </div>
                      {errors.gymExperience && <span className="error-message">{errors.gymExperience.message}</span>}
                    </div>
                  </div>
                )}

                {currentStep === 4 && (
                  <div className="form-fields">
                    <div className="field-group">
                      <label htmlFor="fitnessGoals">What are your health and fitness goals? 🎯</label>
                      <textarea
                        id="fitnessGoals"
                        {...register('fitnessGoals', { required: 'Please tell us about your goals' })}
                        placeholder="Describe your fitness goals, what you want to achieve..."
                        rows={4}
                        className={errors.fitnessGoals ? 'error' : ''}
                      />
                      {errors.fitnessGoals && <span className="error-message">{errors.fitnessGoals.message}</span>}
                    </div>
                  </div>
                )}

                {currentStep === 5 && (
                  <div className="form-fields">
                    <div className="field-group">
                      <label htmlFor="work">What do you do for work? 💼</label>
                      <input
                        id="work"
                        {...register('work', { required: 'Please tell us about your work' })}
                        placeholder="Your profession or occupation"
                        className={errors.work ? 'error' : ''}
                      />
                      {errors.work && <span className="error-message">{errors.work.message}</span>}
                    </div>
                  </div>
                )}

                {currentStep === 6 && (
                  <div className="form-fields">
                    <div className="field-group">
                      <label htmlFor="previousCoaching">Have you ever trained with a coach before? 🤝</label>
                      <textarea
                        id="previousCoaching"
                        {...register('previousCoaching', { required: 'Please answer this question' })}
                        placeholder="Tell us about your previous coaching experience, or write 'No' if you haven't worked with a coach before"
                        rows={3}
                        className={errors.previousCoaching ? 'error' : ''}
                      />
                      {errors.previousCoaching && <span className="error-message">{errors.previousCoaching.message}</span>}
                    </div>
                  </div>
                )}

                {currentStep === 7 && (
                  <div className="form-fields">
                    <div className="field-group">
                      <label htmlFor="medicalConditions">Do you have any current or past medical conditions, injuries, surgeries, or medications that I should be aware of? 🏥</label>
                      <textarea
                        id="medicalConditions"
                        {...register('medicalConditions', { required: 'Please answer this question (write "None" if not applicable)' })}
                        placeholder="Please list any medical conditions, injuries, surgeries, or medications. Write 'None' if not applicable."
                        rows={4}
                        className={errors.medicalConditions ? 'error' : ''}
                      />
                      {errors.medicalConditions && <span className="error-message">{errors.medicalConditions.message}</span>}
                    </div>
                  </div>
                )}

                {currentStep === 8 && (
                  <div className="form-fields">
                    <div className="field-group">
                      <label htmlFor="keenness">On a scale of 1 to 10, how keen are you to start your fitness journey with me? 🔥</label>
                      <div className="scale-container">
                        <div className="scale-labels">
                          <span>Not keen</span>
                          <span>Super keen!</span>
                        </div>
                        <div className="scale-options">
                          {[1,2,3,4,5,6,7,8,9,10].map(num => (
                            <label key={num} className="scale-label">
                              <input
                                type="radio"
                                value={num}
                                {...register('keenness', { required: 'Please select your keenness level' })}
                              />
                              <span className="scale-number">{num}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      {errors.keenness && <span className="error-message">{errors.keenness.message}</span>}
                    </div>
                  </div>
                )}

                {currentStep === 9 && (
                  <div className="form-fields">
                    <div className="field-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          {...register('confirmation', { required: 'Please confirm your information' })}
                        />
                        <span className="checkmark"></span>
                        <span className="checkbox-text">
                          I confirm that I have read all the questions carefully and provided complete, accurate, and honest information to the best of my knowledge. I understand that this information will be used to design a safe and effective coaching program tailored to my needs. ✅
                        </span>
                      </label>
                      {errors.confirmation && <span className="error-message">{errors.confirmation.message}</span>}
                    </div>
                  </div>
                )}
              </div>

              {/* Navigation */}
              <div className="step-navigation">
                {currentStep > 0 && (
                  <button
                    type="button"
                    onClick={prevStep}
                    className="nav-button prev-button"
                  >
                    <ChevronLeft className="w-5 h-5" />
                    Previous
                  </button>
                )}
                
                <div className="nav-spacer"></div>
                
                {currentStep === steps.length - 1 ? (
                  <button
                    type="submit"
                    className="nav-button submit-button"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="spinner"></div>
                        Submitting...
                      </>
                    ) : (
                      <>
                        Submit Application
                        <Sparkles className="w-5 h-5" />
                      </>
                    )}
                  </button>
                ) : currentStep === 0 ? null : (
                  <button
                    type="button"
                    onClick={nextStep}
                    className="nav-button next-button"
                  >
                    Next
                    <ChevronRight className="w-5 h-5" />
                  </button>
                )}
              </div>
            </motion.div>
          </AnimatePresence>
        </form>
      </div>
    </div>
  );
};

export default CoachingInquiryForm; 